"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AnimatePresence, LazyMotion, m } from "framer-motion";

// Feature bundle loads after first paint instead of shipping in the entry chunk.
const loadMotionFeatures = () => import("framer-motion").then((mod) => mod.domAnimation);

const STORAGE_KEY = "lorenzo-intro-done";

type IntroContextValue = {
  /** True after the boot logo has landed in the header (or was skipped). */
  introDone: boolean;
};

const IntroContext = createContext<IntroContextValue>({ introDone: true });

export function useIntro() {
  return useContext(IntroContext);
}

function logoTarget() {
  if (typeof window === "undefined") {
    return {
      top: "20px",
      left: "30px",
      x: 0,
      y: 0,
      fontSize: "18px",
      letterSpacing: "0.32em",
      color: "#000",
    };
  }
  const lg = window.matchMedia("(min-width: 1024px)").matches;
  const md = window.matchMedia("(min-width: 768px)").matches;
  if (lg) {
    return {
      top: "20px",
      left: "30px",
      x: 0,
      y: 0,
      fontSize: "18px",
      letterSpacing: "0.32em",
      color: "#000",
    };
  }
  if (md) {
    return {
      top: "12px",
      left: "60px",
      x: 0,
      y: 0,
      fontSize: "13px",
      letterSpacing: "0.28em",
      color: "#000",
    };
  }
  return {
    top: "12px",
    left: `${window.innerWidth * 0.05}px`,
    x: 0,
    y: 0,
    fontSize: "12px",
    letterSpacing: "0.28em",
    color: "#000",
  };
}

/**
 * big.dk-style boot: black screen, LORENZO centered (white), then flies to the
 * top-left header slot while the black veil lifts. Once per tab session.
 */
export function IntroProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [phase, setPhase] = useState<"center" | "fly" | "done">("done");
  const [target, setTarget] = useState(logoTarget);

  useEffect(() => {
    const skipMotion =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches || navigator.webdriver === true;
    try {
      if (skipMotion || sessionStorage.getItem(STORAGE_KEY)) {
        setPhase("done");
        setReady(true);
        return;
      }
    } catch {
      /* ignore */
    }
    setTarget(logoTarget());
    setPhase("center");
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready || phase !== "center") return;
    const t = window.setTimeout(() => setPhase("fly"), 750);
    return () => window.clearTimeout(t);
  }, [ready, phase]);

  useEffect(() => {
    if (!ready || phase === "done") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [ready, phase]);

  const finish = useCallback(() => {
    setPhase("done");
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(
    () => ({ introDone: !ready || phase === "done" }),
    [ready, phase],
  );

  return (
    <IntroContext.Provider value={value}>
      {children}

      <LazyMotion features={loadMotionFeatures}>
      <AnimatePresence>
        {ready && phase !== "done" && (
          <m.div
            key="intro-veil"
            className="pointer-events-auto fixed inset-0 z-[100] bg-black"
            initial={{ opacity: 1 }}
            animate={{ opacity: phase === "fly" ? 0 : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: [0.45, 0, 0.55, 1] }}
            onAnimationComplete={() => {
              if (phase === "fly") finish();
            }}
            aria-hidden
          >
            <m.span
              className="fixed z-[101] font-semibold uppercase select-none"
              initial={{
                top: "50%",
                left: "50%",
                x: "-50%",
                y: "-50%",
                fontSize: "22px",
                letterSpacing: "0.36em",
                color: "#fff",
              }}
              animate={
                phase === "fly"
                  ? target
                  : {
                      top: "50%",
                      left: "50%",
                      x: "-50%",
                      y: "-50%",
                      fontSize: "22px",
                      letterSpacing: "0.36em",
                      color: "#fff",
                    }
              }
              transition={{ duration: 0.95, ease: [0.45, 0, 0.55, 1] }}
            >
              Lorenzo
            </m.span>
          </m.div>
        )}
      </AnimatePresence>
      </LazyMotion>
    </IntroContext.Provider>
  );
}
